import React, { useState } from "react";
import {
  Container,
  Typography,
  Button,
  IconButton,
  Stack,
  Box,
  Tooltip,
  styled,
  CircularProgress,
  alpha,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  DeleteOutline,
  CloudUpload,
  FileDownloadOutlined,
  InsertDriveFileOutlined,
  Visibility as ViewIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useQuery, useQueryClient } from "react-query";
import { QK } from "../../../base/qk";
import { getCandidatesbyId, updateCandidate } from "../../../api/services/candidate";
import { minioService } from "../../../api/services/minioService";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import DataTable from "../../../components/Table/DataTable";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const DocumentView = ({ clickedId }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [confirmState, setConfirmState] = useState({
    show: false,
    id: null,
    type: null,
    file: null,
  });

  const { data: candidateData, isLoading: isCandidateLoading } = useQuery(
    [QK.candidate, clickedId],
    () => getCandidatesbyId(clickedId),
    {
      enabled: !!clickedId,
    }
  );

  const candidate = candidateData?.data;
  const documents = React.useMemo(() => {
  const raw = candidate?.documents;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}, [candidate?.documents]);

  const formatBytes = (bytes) => {
    if (!+bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase() || "";
    if (ext === "pdf") return <PdfIcon sx={{ fontSize: 20 }} />;
    if (["doc", "docx"].includes(ext)) return <DocIcon sx={{ fontSize: 20 }} />;
    if (["jpg", "jpeg", "png", "gif"].includes(ext)) return <ImageIcon sx={{ fontSize: 20 }} />;
    return <InsertDriveFileOutlined sx={{ fontSize: 20 }} />;
  };

  const getFileColor = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase() || "";
    if (ext === "pdf") return "#EF4444";
    if (["doc", "docx"].includes(ext)) return "#3B82F6";
    if (["jpg", "jpeg", "png", "gif"].includes(ext)) return "#10B981";
    return "#6B7280";
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png"];
    const fileExt = "." + file.name.split(".").pop().toLowerCase();
    if (!validTypes.includes(fileExt)) {
      enqueueSnackbar("Please upload PDF, DOC, DOCX, JPG, JPEG, or PNG files only", {
        variant: "error",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      enqueueSnackbar("File size should be less than 10MB", { variant: "error" });
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !clickedId) return;

    setUploading(true);

    try {
      const response = await minioService.uploadFile(selectedFile, "candidate_documents");

      if (response.success) {
        const fileUrl = response.data.url;

        const currentDocuments = documents || [];
        const newDocument = {
          id: Date.now(),
          name: selectedFile.name,
          size: selectedFile.size,
          url: fileUrl,
          uploaded_at: new Date().toISOString(),
        };

        const updatedDocuments = [...currentDocuments, newDocument];

        const formData = new FormData();
        formData.append("documents", JSON.stringify(updatedDocuments));

        await updateCandidate(formData, clickedId);

        queryClient.invalidateQueries([QK.candidate, clickedId]);
        enqueueSnackbar("Document uploaded successfully", { variant: "success" });
        setSelectedFile(null);
      } else {
        throw new Error(response.message || "Upload failed");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to upload document";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setUploading(false);
      setConfirmState({ show: false, id: null, type: null, file: null });
    }
  };

  const handleDelete = async (docId) => {
    if (!clickedId) return;

    try {
      const updatedDocuments = documents.filter((doc) => doc.id !== docId);

      const formData = new FormData();
      formData.append("documents", JSON.stringify(updatedDocuments));

      await updateCandidate(formData, clickedId);

      queryClient.invalidateQueries([QK.candidate, clickedId]);
      enqueueSnackbar("Document deleted successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to delete document", { variant: "error" });
    } finally {
      setConfirmState({ show: false, id: null, type: null, file: null });
    }
  };

  const handleDownload = (url, name) => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.download = name || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePreview = (url, name) => {
    if (!url) return;

    const ext = url?.split(".").pop()?.toLowerCase() || "";
    const isViewable = ["pdf", "jpg", "jpeg", "png", "gif"].includes(ext);

    if (isViewable) {
      setPreviewUrl(url);
      setPreviewType(ext);
      setPreviewOpen(true);
    } else {
      handleDownload(url, name);
    }
  };

  const handleConfirmAction = () => {
    if (confirmState.type === "delete") {
      handleDelete(confirmState.id);
    } else if (confirmState.type === "upload") {
      handleUpload();
    }
  };

  const closeModal = () => setConfirmState({ show: false, id: null, type: null, file: null });
  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl("");
    setPreviewType("");
  };

  const headers = [
    { key: "name", label: "File Name" },
    { key: "size", label: "Size" },
    { key: "uploaded_at", label: "Uploaded" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "name") {
      const fileColor = getFileColor(item.name);
      return (
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              bgcolor: alpha(fileColor, 0.1),
              color: fileColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {getFileIcon(item.name)}
          </Box>
          <Typography variant="body2" fontWeight={600} color="#1A1A2E">
            {item.name}
          </Typography>
        </Stack>
      );
    }
    if (header.key === "size") {
      return (
        <Typography variant="body2" color="#6B7280">
          {formatBytes(item.size)}
        </Typography>
      );
    }
    if (header.key === "uploaded_at") {
      return (
        <Typography variant="body2" color="#6B7280">
          {item.uploaded_at
            ? new Date(item.uploaded_at).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "-"}
        </Typography>
      );
    }
    return item[header.key] || "-";
  };

  // Custom actions using DataTable's customActions prop
  const customActions = (item) => (
    <Box sx={{ display: "flex", gap: 0.25, justifyContent: "center", flexWrap: "wrap" }}>
      <Tooltip title="Preview" arrow>
        <IconButton
          size="small"
          onClick={() => handlePreview(item.url, item.name)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#2469F0",
              bgcolor: alpha("#2469F0", 0.06),
            },
          }}
        >
          <ViewIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download" arrow>
        <IconButton
          size="small"
          onClick={() => handleDownload(item.url, item.name)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#2469F0",
              bgcolor: alpha("#2469F0", 0.06),
            },
          }}
        >
          <FileDownloadOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete" arrow>
        <IconButton
          size="small"
          onClick={() => setConfirmState({ show: true, type: "delete", id: item.id, file: null })}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#EF4444",
              bgcolor: alpha("#EF4444", 0.06),
            },
          }}
        >
          <DeleteOutline sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (!clickedId) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 10,
          color: "#94A3B8",
        }}
      >
        <InsertDriveFileOutlined sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
        <Typography variant="h6" color="text.secondary">
          No candidate selected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please select a candidate to view documents
        </Typography>
      </Box>
    );
  }

  if (isCandidateLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 0 }}>
      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={closePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "90vh",
            bgcolor: "#1A1A2E",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} color="white">
            Document Preview
          </Typography>
          <IconButton onClick={closePreview} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          {previewType === "pdf" ? (
            <Box sx={{ width: "100%", height: "70vh" }}>
              <iframe
                src={`${previewUrl}#toolbar=1`}
                width="100%"
                height="100%"
                style={{ border: "none", borderRadius: "8px" }}
                title="Document Preview"
              />
            </Box>
          ) : ["jpg", "jpeg", "png", "gif"].includes(previewType) ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Document Preview"
              sx={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                gap: 2,
              }}
            >
              <InsertDriveFileOutlined sx={{ fontSize: 80, color: "rgba(255,255,255,0.3)" }} />
              <Typography variant="body1" color="rgba(255,255,255,0.7)">
                Preview not available for this file type
              </Typography>
              <Button
                variant="contained"
                startIcon={<FileDownloadOutlined />}
                onClick={() => {
                  handleDownload(previewUrl, "document");
                  closePreview();
                }}
                sx={{ mt: 2 }}
              >
                Download to View
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmModal
        show={confirmState.show}
        handleClose={closeModal}
        handleConfirm={handleConfirmAction}
        isDelete={confirmState.type === "delete"}
        title={confirmState.type === "delete" ? "Delete Document" : "Upload Document"}
        body={
          confirmState.type === "delete"
            ? "This action is permanent. Are you sure you want to remove this file?"
            : `Do you want to upload "${selectedFile?.name}" to the candidate profile?`
        }
        confirmText={confirmState.type === "delete" ? "Delete Now" : "Upload Now"}
        isLoading={uploading}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="#1A1A2E">
            Documents
          </Typography>
          <Typography variant="caption" color="#6B7280">
            Manage candidate files
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUpload />}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                borderColor: "#2469F0",
                bgcolor: alpha("#2469F0", 0.04),
              },
            }}
          >
            {selectedFile ? selectedFile.name : "Choose File"}
            <VisuallyHiddenInput type="file" onChange={handleFileChange} />
          </Button>
          <Button
            variant="contained"
            disabled={!selectedFile || uploading}
            onClick={() =>
              setConfirmState({ show: true, type: "upload", id: null, file: selectedFile })
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              bgcolor: "#2469F0",
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
              "&.Mui-disabled": {
                bgcolor: "#F1F5F9",
                color: "#94A3B8",
              },
            }}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </Stack>
      </Box>

      <DataTable
        data={documents}
        headers={headers}
        renderCell={renderCell}
        customActions={customActions}
        loading={isCandidateLoading}
      />
    </Container>
  );
};

export default DocumentView;